import "./PayoutsList.scss";

import React from "react";
import { Link } from "react-router-dom";
import usesFeatures from "usesFeatures";
import { formatDate } from "date_helpers";

import Table from "Table";
import Header from "Header";
import Input from "Input";
import Paginate from "Paginate";

// Shared payout index used by both the internal tools and venue admin. The two
// only differ in which endpoint they hit and whether they can create payouts
// (venue admins cannot) — everything else (search, sort, pagination) is
// identical, so it lives here.
class PayoutsList extends React.Component {
  state = {
    search: "",
    sortBy: "created_at",
    sortOrder: "desc",
  };

  componentDidMount() {
    this.fetchPayouts(1);
  }

  componentWillUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  buildURL = (page) => {
    const { search, sortBy, sortOrder } = this.state;
    const params = new URLSearchParams({
      sort_by: sortBy,
      sort_order: sortOrder,
      page,
    });
    if (search) params.append("search", search);

    return `${this.props.endpoint}?${params.toString()}`;
  };

  fetchPayouts = (page = 1) => {
    this.props.fetchData(this.buildURL(page));
  };

  // Debounce typed search so we don't fire a request per keystroke. Clearing
  // the field still refetches — the empty value drops the search param.
  handleSearchChange = (value) => {
    this.setState({ search: value });
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.fetchPayouts(1), 350);
  };

  // Clicking a column toggles direction; first click on a new column sorts
  // descending. Always returns to page 1 so the user sees the new top.
  handleSort = (column) => {
    const { sortBy, sortOrder } = this.state;
    const newOrder = sortBy === column && sortOrder === "desc" ? "asc" : "desc";
    this.setState({ sortBy: column, sortOrder: newOrder }, () => this.fetchPayouts(1));
  };

  sortHeader = (label, column) => {
    const { sortBy, sortOrder } = this.state;
    const active = sortBy === column;
    const caret = active ? (sortOrder === "asc" ? "▲" : "▼") : "";
    return (
      <span className="PayoutsList-sort" onClick={() => this.handleSort(column)}>
        {label} <span className="PayoutsList-caret">{caret}</span>
      </span>
    );
  };

  headers = () => ({
    accountName: this.sortHeader("Account", "account_name"),
    dateRange: this.sortHeader("Dates", "dates"),
    amount: this.sortHeader("Amount", "amount"),
    eventCount: this.sortHeader("Events", "events"),
    // "Sent" is shown only where the caller opts in (internal tools).
    ...(this.props.showSent ? { sentOn: "Sent" } : {}),
    createdAt: this.sortHeader("Created", "created_at"),
    view: "",
  });

  formatters = () => ({
    accountName: (p) => p.accountName,
    amount: (p) => p.amount,
    eventCount: (p) => p.eventCount,
    createdAt: (p) => formatDate(p.createdAt),
    view: (p) => <Link to={`/payouts/${p.id}`}>View</Link>,
  });

  render() {
    if (!this.props.dataFetched) return false;

    return (
      <div className="PayoutsList">
        <Header text="Payouts" />

        {this.props.searchable && (
          <div className="box">
            <div className="columns">
              <div className="column is-4">
                <Input
                  label="Search"
                  value={this.state.search}
                  onChange={this.handleSearchChange}
                  placeholder="Account or venue name..."
                />
              </div>
            </div>
          </div>
        )}

        <Table
          newRecordPath={this.props.canCreate ? "/payouts/new" : null}
          newRecordText="New Payout"
          empty={<h3>No payouts</h3>}
          showHeader={true}
          headers={this.headers()}
          formatters={this.formatters()}
          rows={this.props.pageData}
        />

        <Paginate changePage={this.fetchPayouts} {...this.props.pagy} />
      </div>
    );
  }
}

PayoutsList.defaultProps = {
  searchable: true,
  showSent: false,
  canCreate: false,
};

export default usesFeatures(["data"], PayoutsList);
